import { LazyMotion, domAnimation, m } from "framer-motion";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { MainContext } from "../../context/mainContext";
import {
    actionUrlConverter,
    alternateUrlConverter,
    domainConverter,
    isValidUrl,
    shopNowClick
} from '../../util';
import {
    CONTAINER,
    PRODUCT,
    SLIDER_VISIBLE_ITEMS,
    INTERACTIVE_CATALOGS,
    getSliderSettings
} from './constants';
import { RegaliaUxConstants, RegaliaUxDialog } from '../../components/regalia-ux'
import { useGetCustomerNameListSettings } from '../../components/regalia-ux/hooks'
import { useGetRegaliaUxDialogData } from '../../hooks'
import { addUtmParameters } from '../../util/utmParameters';

export const CatalogList = ({ 
    appData: {
        storedata,
        customerdata,
        isSchoolStorePage
    }, 
    catalogData,
    googleAnalytics
}) => {
    const modalContext = useContext(MainContext)
    const mascotName = storedata?.schoolStoreHeroBannerData?.mascotName
    const productCatalog = customerdata?.filter(data=>data.contentType === 'ProductCatalog')
    const catalogs = isSchoolStorePage ? storedata?.productCatalogs : catalogData?.subCatalogs;
    const catalogsQty = catalogs?.filter(pc=>pc?.catalogHidden===false).length;
    const slickRef = useRef(null)
    const customerNumber = storedata?.schoolStoreHeroBannerData?.affiliateId
    const contactUs = `<a href=${alternateUrlConverter('/apps/shop/help/shared/customer_care_index.asp')}>contact us</a>`
    const changeYourSchool = "<a class='affiliate-school' href='/#'>change your school</a>"
    const [catalogsList, setCatalogList] = useState([]);

    const { customerNameListSettings } = useGetCustomerNameListSettings()
    const isStudentVerificationProgramAvailable = customerNameListSettings && customerNameListSettings.length > 0
    // TODO: remove test for 'false' string once JCOM-51666 will be resolved
    const isRegaliaUxEnabled = (storedata[RegaliaUxConstants.FEATURE_FLAG_NAME] && storedata[RegaliaUxConstants.FEATURE_FLAG_NAME] !== 'false') || isStudentVerificationProgramAvailable

    const [isRegaliaDialogVisible, setIsRegaliaDialogVisible] = useState(false);
    const [activeCatalogOsr, setActiveCatalogOsr] = useState()
    const regaliaUxDialogData = useGetRegaliaUxDialogData(activeCatalogOsr, catalogs, isRegaliaDialogVisible)

    const itemsClassName = catalogsQty && catalogsQty > 3 ? '' : ` items-${catalogsQty}`
    const cardsClassName = useMemo(() => (i) => {
        if ((catalogsQty === 2) || (catalogsQty === 3 && i <= 1) || (catalogsQty === 4 && i === 0) ) {
            return ' first'
        } else {
            return ''
        }
    }, [catalogsQty])

    const a11yMaker = useCallback(
        () => {
            const activeCards = slickRef.current.querySelectorAll('.slick-active')
            const inActiveCards = slickRef.current.querySelectorAll('.slick-slide.item:not(.slick-active)')
            Array.from(activeCards).forEach(el => {
                el.setAttribute("tabindex","0");
            });
            Array.from(inActiveCards).forEach(el => {
                el.setAttribute("tabindex","-1");
            });
        },
        [],
    )

    useEffect(() => {
        setTimeout(() => {
            if(document.readyState !== 'loading'){
                modalContext.setLoading(false);
            }
        }, 3000)
    }, [])

    useEffect(()=>{
        let list = isSchoolStorePage ? storedata?.productCatalogs : catalogData?.subCatalogs;
        list = list.sort((a,b) => a.displaySequence - b.displaySequence);
        setCatalogList(list)
    },[storedata?.productCatalogs, catalogData?.subCatalogs]);

    useEffect(() => {
        let err = null
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.has('error') && urlParams.get('error') === 'true') {
            switch (urlParams.get('errorFile')) {
                case 'no-product-error-specific.htm':
                    err = {
                        case:1,
                        message: urlParams.get('errorProductName')
                                ? `${urlParams.get('errorProductName')} are currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                                : `Selected products are currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                    }
                    break;

                case 'no-product-error.htm':
                    err = {
                        case:2,
                        message: `Product is currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                    }
                    break;

                case 'no-group-error-specific.htm':
                    err = {
                        case:3,
                        message: urlParams.get('errorGroupName')
                                ? `${urlParams.get('errorGroupName') === '-' ? 'That group' : urlParams.get('errorGroupName')} is currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                                : `Selected catalogs are currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                    }
                    break;

                case 'no-group-error.htm':
                    err = {
                        case:4,
                        message: `Product collection is currently not available to purchase online. Please ${contactUs} for product availability and ordering information.`
                    }
                    break;

                case null:
                    err = {
                        case:6,
                        message: `Our website encountered an unexpected error. We apologize for the inconvenience. Check to make sure you're at the right school. You can ${changeYourSchool}, browse the products below, or ${contactUs} for additional support.`
                    }
                    break;

                case 'inaccessible-product-error.htm':
                    err = {
                        case:7,
                        message: 'That item is inaccessible. Please sign in as the original owner of that item.'
                    }
                    break;

                case 'no-catalog-products-error.htm':
                    err = {
                        case:8,
                        message: `Actual message: The product you looked for is not currently available for this school. Please ${contactUs} for product availability and ordering information.`
                    }
                    break;

                case 'no-customer-found.htm':
                    err = {
                        case:9,
                        message: `Actual Message: Your school or group could not be found.We apologize for the inconvenience. Check to make sure you're at the right school. You can ${changeYourSchool}, browse the products below, or ${contactUs} for additional support.`
                    }
                    break;

                default:
                    break;
            }
        }
        let showError = isSchoolStorePage
            ? ((!storedata?.productCatalogs) || (storedata?.productCatalogs?.length === 0))
            : ((!catalogData?.subCatalogs) || (catalogData?.subCatalogs?.length === 0))
        if ( showError ) {
            const affilate = document.cookie.replace(/(?:(?:^|.*;\s*)affiliateName\s*\=\s*([^;]*).*$)|^.*$/, "$1") + '|' + document.cookie.replace(/(?:(?:^|.*;\s*)affiliateIds\s*\=\s*([^;]*).*$)|^.*$/, "$1")
            const options = {
                method: "POST",
                mode: 'no-cors',
                headers: new Headers({'Content-Type': 'text/plain'}),
                body: 'There are no Jostens catalogs available for ' + affilate
            };

            fetch(actionUrlConverter('https://devwww.jostens.com/apps/store/report.mvc'), options)

            err = {
                case:5,
                message: 'There are currently no Jostens catalogs available for this school at this time. We apologize for the inconvenience.'
            }
        }

        if (err) {
            console.log(err);
            const errorContainer = document.querySelector('.product-categories .container')
            const h2 = document.querySelector('.product-categories .container h2')
            const toast = `<div class="alert alert-danger alert-icon alert-dismissible fade show" role="alert">
                                <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                                    <span aria-hidden="true">×</span>
                                </button>
                                <span>${err.message}</span>
                            </div>`
            toast && errorContainer.insertAdjacentHTML("afterbegin", toast)
            h2 && errorContainer.prepend(h2)
        }

    }, [changeYourSchool, contactUs, storedata?.productCatalogs, catalogData?.subCatalogs])

    const showDots = (catalogsQty > SLIDER_VISIBLE_ITEMS)
    const slidesToShowAndScroll = catalogsQty >= SLIDER_VISIBLE_ITEMS 
        ? SLIDER_VISIBLE_ITEMS 
        : catalogsQty

    const defaultSettings = {
        slidesToShow: slidesToShowAndScroll,
        slidesToScroll: slidesToShowAndScroll,
        arrows: showDots,
        dots: showDots,
    }

    const additionalSettings = {
        afterChange: a11yMaker,
        variableWidth: !Boolean(catalogsQty === 2),
    }

    const settings = getSliderSettings(defaultSettings, additionalSettings)

	useEffect(() => {
        if(slickRef.current && slickRef.current.querySelector('.slick-slider')) {
            let visibleItemsCount = 5

            if(catalogsQty >= 5 && catalogsQty <=7) visibleItemsCount = catalogsQty
            if(catalogsQty > 7) visibleItemsCount = 7
                        
            const root = document.documentElement
            const gridElementWidth = Number(slickRef.current.offsetWidth) / visibleItemsCount - 10;

            root.style.setProperty('--slider-element-width', gridElementWidth + "px")
            root.style.setProperty('--slider-element-width-double', gridElementWidth * 2 + "px")

            try {
                const style = document.createElement("style");
                if (document?.head !== null) document.head.appendChild(style);
                if (style?.sheet !== null) style.sheet.insertRule('.slick-disabled{display:none !important;}', 0);
            } catch(error) {
                console.log(error)
            }
            

            a11yMaker()
        }
    }, [slickRef.current, a11yMaker, catalogsQty])

    const imgHandler = useCallback(
        (catOsr) => {
            let url
            if (productCatalog[0]?.images) {
                const cat = productCatalog[0].images.find(el => el.catalogOsr === catOsr)
                url = cat?.imageUrl
            }
            return url
        },
        [productCatalog]
    )

    const trackGASY25SelectItemEvent = async (catalogOsr, catalogDisplayName, clickText) => {
        try {
            googleAnalytics?.selectItemTrackingGASY25(
                catalogOsr,
                googleAnalytics?.customerClassCode,
                clickText, 
                catalogDisplayName,
                'button_click'
            );
            if (window?.jostensGaTracking?.gaEventsSpecPromise) {
                await Promise.all([window.jostensGaTracking.gaEventsSpecPromise])
            }
        } catch (err) {
            console.warn('Failed tracking GA event', err)
        }
    }


    const redirectToCatalog = (catalog, clickText) => {
        let hasOsr = productCatalog[0]?.excludeList?.hasOwnProperty(catalog.catalogOsr) || false;
        let hasCustomer = false;
        if (hasOsr) {
            let customers = productCatalog[0]?.excludeList[catalog.catalogOsr];
            hasCustomer = customers.includes(customerNumber) || customers === "ALL"
        }
        if (hasCustomer) {
            modalContext.setLoading(true)
            trackGASY25SelectItemEvent(catalog?.catalogOsr, catalog?.displayName, clickText).then(() => {
                window.open(actionUrlConverter(catalog.actionUrl), '_self')
            })
        } else {
            modalContext.openPopup({
                class: 'small',
                title: 'Select Your Collection',
                content: <div className="list-group">
                            {
                                catalog.subCatalogs.map( cat => {
                                    let actionUrl = cat.actionUrl;
                                    if(actionUrl.includes("?subCatalog=true") && hasOsr) {
                                        actionUrl = actionUrl + "&router=123"
                                    } else if(hasOsr) {
                                        actionUrl = actionUrl + "?router=123"
                                    }
                                    return (
                                        <a key={cat.id} 
                                            href={actionUrlConverter(actionUrl)}
                                            className="jos-ga-tracking-element list-group-item" 
                                            data-jos-ga-tracking={JSON.stringify({
                                                _resolveFields: {
                                                    businessUnit: { 
                                                        catalogOsr: cat?.catalogOsr, 
                                                        customerClassCode: googleAnalytics?.customerClassCode
                                                    },
                                                    productLine: { 
                                                        catalogOsr: cat?.catalogOSR 
                                                    }
                                                },
                                                clickText: cat?.displayName, 
                                                modalType: 'Select Your Collection', 
                                                itemName: cat?.displayName, 
                                                actionType: 'button_click', 
                                                categoryName: 'Catalog List'
                                            })}
                                        >
                                            <span>{cat.displayName}</span>
                                        </a>
                                    )
                                } )
                            }
                        </div>
            })
        }
    }

    const cardClickHandler = (e, catalog) => {
        e.stopPropagation()
        !isSchoolStorePage && shopNowClick(catalog?.displayName);

        if ( INTERACTIVE_CATALOGS.includes(catalog.catalogOsr) && isRegaliaUxEnabled ) {
            setActiveCatalogOsr(catalog.catalogOsr)
            setIsRegaliaDialogVisible(true)
        }else{
            const clickText = e?.target?.innerText || 'SHOP'
            redirectToCatalog(catalog, clickText)
        }
    }

    const cardClickHandlerGetLink = (catalog) => {
            let setLink = null;
            let target = null
            if (catalog.subCatalogs?.length) {
                setLink = ""
                target = ""
            } else {
                if (catalog.alternateUrl) {
                    if((isValidUrl(catalog.alternateUrl)) && (new URL(catalog.alternateUrl)?.href?.indexOf('https'!== -1) || new URL(catalog.alternateUrl)?.href?.indexOf('http'!== -1))){
                        setLink = catalog.alternateUrl
                        target = '_blank'
                        modalContext.setLoading(false)
                    }
                    else {
                        setLink = alternateUrlConverter(catalog.alternateUrl)
                        target = '_blank'
                        modalContext.setLoading(false)
                    }
                } else {
                    setLink = actionUrlConverter(catalog.actionUrl)
                    target = '_self'
                }
            }
            if (isValidUrl(setLink)) {
                setLink = addUtmParameters(setLink)
            }
            return {setLink, target};
    }

    const getCatalogCTA = catalog => (catalog?.catalogOsr === 'HS_JACKETS_PROSPECT') ? 'REQUEST NOW' : 'Shop';

    const renderCatalog = (key, catalog)=>{
        return (
            <m.div
                className={'item' + cardsClassName(key)}
                style={{cursor: 'pointer'}}
                key={`CatalogItem-${key}`}
                variants={PRODUCT}
            >
                <div className="img">
                    <img loading="lazy" width="100%" height="100%" src={imgHandler(catalog.catalogOsr) ? domainConverter(imgHandler(catalog.catalogOsr)) : domainConverter(catalog.imageUrl)} alt={catalog.displayName} />
                </div>
                {window.innerWidth > 991
                    ?
                    <div className="overlay">
                        <div className="inner">
                            <h3>{catalog.displayName}</h3>
                            <div className="btn btn-sm btn-white btn-white--ghost">{getCatalogCTA(catalog)}</div>
                        </div>
                    </div>
                    :
                    <div className="substring">{catalog.displayName}</div>
                }
            </m.div>
        )
    }

    const handleCatalogClick = (catalog) => (event) => {
        if(catalog.subCatalogs?.length){
            cardClickHandler(event, catalog)
        }else{
            modalContext.setLoading(true);
            !isSchoolStorePage && shopNowClick(catalog?.displayName);
        }
    }

    const handleEnterKeyPress = (catalog) => (event) => {
        if (event.key === 'Enter') {
            return handleCatalogClick(catalog)(event)
        }
    }

	return (
        <LazyMotion features={domAnimation}>
            <div className={'product-categories' + itemsClassName} >
                <m.div
                    className="container"
                    ref={slickRef}
                    variants={CONTAINER}
                    initial="hidden"
                    animate="show"
                >
                    { mascotName && storedata ? <h2>SHOW YOUR {mascotName} PRIDE</h2> : storedata ? <h2>SHOW YOUR PRIDE</h2> : null}
                    { catalogsQty === 1 &&
                        catalogsList.map((catalog) =>
                            catalog?.catalogHidden ? null :
                                <div key={catalog.catalogOsr} className="banner item">
                                    <div className="img">
                                        <img loading="lazy" src={imgHandler(catalog.catalogOsr) ? domainConverter(imgHandler(catalog.catalogOsr)) : domainConverter(catalog.imageUrl)} alt={catalog.displayName} />
                                    </div>
                                    <div className="overlay">
                                        <div className="inner">
                                            <h3>{catalog.displayName}</h3>
                                            <p>{catalog.description}</p>
                                            <div>
                                            {catalog.subCatalogs?.length
                                                ? <a
                                                    onClick={(e)=>{cardClickHandler(e, catalog)}}
                                                    className="btn btn-sm btn-white btn-white--ghost"
                                                >Shop</a>
                                                : <a
                                                    onClick={handleCatalogClick(catalog)}
                                                    href={cardClickHandlerGetLink(catalog).setLink}
                                                    target={cardClickHandlerGetLink(catalog).target}
                                                    className="jos-ga-tracking-element btn btn-sm btn-white btn-white--ghost"
                                                    data-jos-ga-tracking={JSON.stringify({
                                                        _resolveFields: {
                                                            businessUnit: { 
                                                                catalogOsr: catalog?.catalogOsr, 
                                                                customerClassCode: googleAnalytics?.customerClassCode
                                                            },
                                                            productLine: { 
                                                                catalogOsr: catalog?.catalogOsr 
                                                            }
                                                        },
                                                        clickText: 'Shop', 
                                                        modalType: '', 
                                                        itemName: catalog?.displayName, 
                                                        actionType: 'button_click', 
                                                        categoryName: 'Catalog List'
                                                    })}
                                                >Shop</a>
                                            }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                    }
                    { catalogsQty > 1 &&
                        <Slider {...settings} className={window.innerWidth < 992 ? "carousel row": "carousel"}>
                            {catalogsList?.map((catalog, key) => {
                                const hasSubCatalogs = catalog.subCatalogs?.length;
                                const catalogLinkProps = {
                                    className: `jos-ga-tracking-element ${window.innerWidth < 992 ? 'col-6 col-md-4 mb-4': ''}`,
                                    onClick: handleCatalogClick(catalog),
                                    ...(hasSubCatalogs && {
                                        onKeyDown: handleEnterKeyPress(catalog),
                                        role: 'button',
                                        tabIndex: 0
                                    }),
                                    ...(!hasSubCatalogs && {
                                        href: cardClickHandlerGetLink(catalog).setLink,
                                        target: cardClickHandlerGetLink(catalog).target
                                    }),
                                }

                                return !catalog?.catalogHidden && (
                                    <a key={catalog.catalogOsr} {...catalogLinkProps}
                                        data-jos-ga-tracking={JSON.stringify({
                                            _resolveFields: {
                                                businessUnit: { 
                                                    catalogOsr: catalog?.catalogOsr, 
                                                    customerClassCode: googleAnalytics?.customerClassCode
                                                },
                                                productLine: { 
                                                    catalogOsr: catalog?.catalogOsr 
                                                }
                                            },
                                            clickText: getCatalogCTA(catalog), 
                                            modalType: '', 
                                            itemName: catalog?.displayName, 
                                            actionType: 'button_click', 
                                            categoryName: 'Catalog List'
                                        })}
                                    >
                                        {renderCatalog(key, catalog)}
                                    </a>
                                )
                            })}
                        </Slider>
                    }
                </m.div>
            </div>

            <RegaliaUxDialog 
                isVisible={isRegaliaDialogVisible} 
                data={regaliaUxDialogData} 
                onHide={()=>setIsRegaliaDialogVisible(false)}
            />
        </LazyMotion>
	)
}

export default CatalogList